import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CreditCard,
    Search,
    Filter,
    Eye,
    CheckCircle,
    Clock,
    AlertCircle,
    XCircle,
    RefreshCw,
    Store,
    User,
    DollarSign,
    Image as ImageIcon,
    FileText,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import { 
    useGetMarketplaceTransactionsQuery,
    useUpdateMarketplaceTransactionStatusMutation,
    MarketplaceTransaction,
    MarketplaceTransactionsParams
} from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import toast from 'react-hot-toast';

const MarketplaceTransactions: React.FC = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<'all' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded'>('all');
    const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [selectedTransaction, setSelectedTransaction] = useState<MarketplaceTransaction | null>(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateStatus, setUpdateStatus] = useState<'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded'>('completed');
    const [adminNotes, setAdminNotes] = useState('');
    const [markListingSold, setMarkListingSold] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');

    // Fetch all marketplace transactions (no pagination params for client-side filtering)
    const { 
        data: transactionsData, 
        isLoading, 
        error, 
        refetch 
    } = useGetMarketplaceTransactionsQuery({
        page: 1,
        limit: 10000 // Fetch all for client-side filtering
    });

    const [updateTransactionStatus, { isLoading: isUpdating }] = useUpdateMarketplaceTransactionStatusMutation();

    const allTransactions = transactionsData?.data?.transactions || [];

    // Client-side filtering
    const filteredTransactions = allTransactions.filter((transaction: MarketplaceTransaction) => {
        const matchesSearch =
            transaction.listing?.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.user?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.external_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.transaction_id.toString().includes(searchTerm);
        
        const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
        const matchesPaymentMethod = paymentMethodFilter === 'all' || transaction.payment_method === paymentMethodFilter;
        
        return matchesSearch && matchesStatus && matchesPaymentMethod;
    });

    // Client-side pagination
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);

    // Helper function to get page numbers for pagination
    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }
        
        return pages;
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Completed</Badge>;
            case 'processing':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Processing</Badge>;
            case 'pending':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
            case 'failed':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Failed</Badge>;
            case 'cancelled':
                return <Badge variant="default" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Cancelled</Badge>;
            case 'refunded':
                return <Badge variant="default" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />Refunded</Badge>;
            case 'requires_kyc':
                return <Badge variant="warning" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />KYC Required</Badge>;
            case 'blocked_threshold':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Blocked Threshold</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    const formatCurrency = (amount: number, currencySymbol?: string | null) => {
        const symbol = currencySymbol || '₦';
        return `${symbol}${amount.toLocaleString('en-US')}`;
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const handleViewTransaction = (transaction: MarketplaceTransaction) => {
        navigate(`/admin/marketplace-transactions/view/${transaction.transaction_id}`);
    };

    const handleUpdateStatus = (transaction: MarketplaceTransaction) => {
        setSelectedTransaction(transaction);
        setUpdateStatus(transaction.status);
        setAdminNotes(transaction.admin_notes || '');
        setMarkListingSold(false);
        setShowUpdateModal(true);
    };

    const handleSaveStatusUpdate = async () => {
        if (!selectedTransaction) return;

        try {
            await updateTransactionStatus({
                transaction_id: selectedTransaction.transaction_id,
                status: updateStatus,
                admin_notes: adminNotes.trim() || undefined,
                mark_listing_sold: markListingSold
            }).unwrap();
            toast.success('Transaction status updated successfully');
            setShowUpdateModal(false);
            setSelectedTransaction(null);
            setAdminNotes('');
            setMarkListingSold(false);
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to update transaction status');
        }
    };

    const handleViewImage = (imageUrl: string) => {
        setSelectedImageUrl(imageUrl);
        setShowImageModal(true);
    };

    // Statistics
    const processingCount = allTransactions.filter(t => t.status === 'processing').length;
    const completedCount = allTransactions.filter(t => t.status === 'completed').length;
    const failedCount = allTransactions.filter(t => t.status === 'failed').length;
    // Note: Total amount calculation doesn't use currency symbol as it's a sum across different currencies
    const totalAmount = allTransactions.reduce((sum, t) => sum + t.amount, 0);

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading transactions. Please try again.</p>
                    <button
                        onClick={() => refetch()}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-8">
                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Processing</p>
                                <p className="text-2xl font-bold text-yellow-600">{processingCount}</p>
                            </div>
                            <div className="p-3 bg-yellow-100 rounded-full">
                                <Clock className="w-6 h-6 text-yellow-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Completed</p>
                                <p className="text-2xl font-bold text-green-600">{completedCount}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Failed</p>
                                <p className="text-2xl font-bold text-red-600">{failedCount}</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-full">
                                <XCircle className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search transactions..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value as any);
                                        setCurrentPage(1);
                                    }}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="all">All Status</option>
                                    <option value="processing">Processing</option>
                                    <option value="completed">Completed</option>
                                    <option value="failed">Failed</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="refunded">Refunded</option>
                                </select>
                            </div>
                            <select
                                value={paymentMethodFilter}
                                onChange={(e) => {
                                    setPaymentMethodFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Payment Methods</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="wallet">Wallet</option>
                                <option value="online_gateway">Online Gateway</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl font-semibold text-gray-900">Transactions ({filteredTransactions.length})</h1>
                            <button
                                onClick={() => refetch()}
                                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                                title="Refresh Transactions"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Items per page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {filteredTransactions.length === 0 ? (
                    <div className="p-12 text-center">
                        <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
                        <p className="text-gray-600">No transactions match your current filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listing</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedTransactions.map((transaction) => (
                                    <tr key={transaction.transaction_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">#{transaction.transaction_id}</div>
                                            {transaction.external_reference && (
                                                <div className="text-xs text-gray-500">{transaction.external_reference}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <Store className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {transaction.listing?.title || (transaction.listing?.listing_id ? `Listing #${transaction.listing.listing_id}` : 'N/A')}
                                                    </div>
                                                    {transaction.listing?.listing_type && (
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text border font-medium capitalize text-[10px] ${
                                                                transaction.listing.listing_type === 'car'
                                                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                                    : 'bg-purple-50 text-purple-700 border-purple-200'
                                                            }`}>
                                                                {transaction.listing.listing_type}
                                                            </span>
                                                            {transaction.listing.location && (
                                                                <span className="text-xs text-gray-500">
                                                                    • {transaction.listing.location}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {transaction.user?.name || (transaction.user?.email ? transaction.user.email.split('@')[0] : (transaction.user?.user_id ? `User #${transaction.user.user_id}` : 'N/A'))}
                                                    </div>
                                                    {transaction.user?.email && (
                                                        <div className="text-xs text-gray-500">
                                                            {transaction.user.email}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-semibold text-gray-900">
                                                {formatCurrency(transaction.amount, transaction.user?.currency_symbol)}
                                            </div>
                                            {transaction.wallet_amount > 0 && (
                                                <div className="text-xs text-gray-500">
                                                    Wallet: {formatCurrency(transaction.wallet_amount, transaction.user?.currency_symbol)}
                                                </div>
                                            )}
                                            {transaction.is_large_transaction && (
                                                <div className="text-xs text-orange-600 font-medium mt-1">Large Transaction</div>
                                            )}
                                            {transaction.is_below_min_threshold && (
                                                <div className="text-xs text-red-600 font-medium mt-1">Below Min</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 capitalize">
                                                {transaction.payment_method?.replace('_', ' ') || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(transaction.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(transaction.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewTransaction(transaction)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleUpdateStatus(transaction)}
                                                    className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition-colors"
                                                    title="Update Status"
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mt-6">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing page {currentPage} of {totalPages} ({filteredTransactions.length} total transactions)
                            </div>

                            <div className="flex items-center space-x-2">
                                {/* First Page */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="First page"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>

                                {/* Previous Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Previous page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center space-x-1">
                                    {getPageNumbers().map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                </div>

                                {/* Next Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Next page"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>

                                {/* Last Page */}
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Last page"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Update Status Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => {
                    setShowUpdateModal(false);
                    setSelectedTransaction(null);
                    setAdminNotes('');
                    setMarkListingSold(false);
                }}
                title="Update Transaction Status"
            >
                {selectedTransaction && (
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                                value={updateStatus}
                                onChange={(e) => setUpdateStatus(e.target.value as any)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="processing">Processing</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="refunded">Refunded</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                            <textarea
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                rows={4}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter admin notes..."
                            />
                        </div>
                        {updateStatus === 'completed' && selectedTransaction.listing && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="markSold"
                                    checked={markListingSold}
                                    onChange={(e) => setMarkListingSold(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="markSold" className="ml-2 text-sm font-medium text-gray-700">
                                    Mark listing as sold
                                </label>
                            </div>
                        )}
                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                onClick={() => {
                                    setShowUpdateModal(false);
                                    setSelectedTransaction(null);
                                    setAdminNotes('');
                                    setMarkListingSold(false);
                                }}
                                variant="outline"
                                disabled={isUpdating}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSaveStatusUpdate}
                                variant="primary"
                                disabled={isUpdating}
                                isLoading={isUpdating}
                            >
                                Update Status
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Image Modal */}
            <Modal
                isOpen={showImageModal}
                onClose={() => {
                    setShowImageModal(false);
                    setSelectedImageUrl('');
                }}
                title="Payment Screenshot"
            >
                <div className="p-6">
                    <img
                        src={selectedImageUrl}
                        alt="Payment Screenshot"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
            </Modal>
        </div>
    );
};

export default MarketplaceTransactions;

